import React from 'react';

import { Button, Icon } from '../../../components/common';
import type { ScheduleEvent, RehearsalScene } from '../../scheduler/types';
import type { Act } from '../../show-structure/types';
import { getOrderedScenes } from '../../show-structure/utils/scene-order';
import { useAppStore } from '../../../store/useAppStore';
import { useDashboardAccessibleToolIds } from '../logic/useDashboardAccessibleToolIds';
import { useDashboardNavigation } from '../logic/useDashboardNavigation';
import { DashboardWidgetCard } from './DashboardWidgetCard';

function getRehearsedSceneIds(
    events: ScheduleEvent[],
    acts: Act[],
    rehearsalScenes: RehearsalScene[],
    allSceneIds: Set<string>,
    now: Date
): Set<string> {
    const rehearsedSceneIds = new Set<string>();
    const actById = new Map(acts.map(act => [act.id, act] as const));
    const rehearsalSceneById = new Map(rehearsalScenes.map(scene => [scene.id, scene] as const));

    for (const event of events) {
        if (!hasScheduleEventEnded(event, now)) {
            continue;
        }

        if (event.type === 'scene' && allSceneIds.has(event.targetId)) {
            rehearsedSceneIds.add(event.targetId);
        } else if (event.type === 'act') {
            for (const sceneId of actById.get(event.targetId)?.sceneIds ?? []) {
                if (allSceneIds.has(sceneId)) {
                    rehearsedSceneIds.add(sceneId);
                }
            }
        } else if (event.type === 'rehearsal-scene') {
            for (const sceneId of rehearsalSceneById.get(event.targetId)?.relatedSceneIds ?? []) {
                if (allSceneIds.has(sceneId)) {
                    rehearsedSceneIds.add(sceneId);
                }
            }
        } else if (event.type === 'run') {
            for (const sceneId of allSceneIds) {
                rehearsedSceneIds.add(sceneId);
            }
        }
    }

    return rehearsedSceneIds;
}

function parseScheduleTimeToMinutes(time: string): number | null {
    const [hourText, minuteText] = time.split(':');
    const hour = Number(hourText);
    const minute = Number(minuteText);

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
        return null;
    }

    return (hour * 60) + minute;
}

function parseScheduleDateTime(dateKey: string, time: string): Date | null {
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
    const minutes = parseScheduleTimeToMinutes(time);

    if (!dateMatch || minutes === null) {
        return null;
    }

    const [, yearText, monthText, dayText] = dateMatch;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;

    return new Date(year, month - 1, day, hour, minute);
}

function getScheduleEventEndDate(event: ScheduleEvent): Date | null {
    const startDate = parseScheduleDateTime(event.date, event.startTime);
    const endDate = parseScheduleDateTime(event.date, event.endTime);

    if (!startDate || !endDate) {
        return null;
    }

    if (endDate.getTime() <= startDate.getTime()) {
        endDate.setDate(endDate.getDate() + 1);
    }

    return endDate;
}

function hasScheduleEventEnded(event: ScheduleEvent, now: Date): boolean {
    const endDate = getScheduleEventEndDate(event);
    return Boolean(endDate && endDate.getTime() <= now.getTime());
}

export const RehearsalProgressWidget: React.FC = () => {
    const events = useAppStore(state => state.rehearsalSchedule || []);
    const acts = useAppStore(state => state.showStructure.acts || []);
    const scenes = useAppStore(state => state.showStructure.scenes || []);
    const rehearsalScenes = useAppStore(state => state.rehearsalScenes || []);
    const openTool = useDashboardNavigation();
    const accessibleToolIds = useDashboardAccessibleToolIds();

    const orderedScenes = getOrderedScenes(acts, scenes);
    const sceneIds = new Set(orderedScenes.map(scene => scene.id));
    const rehearsedSceneIds = getRehearsedSceneIds(events, acts, rehearsalScenes, sceneIds, new Date());
    const completedCount = rehearsedSceneIds.size;
    const totalCount = orderedScenes.length;
    const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
    const nextScene = orderedScenes.find(scene => !rehearsedSceneIds.has(scene.id));
    const canOpenScheduler = accessibleToolIds.has('scheduler');

    return (
        <DashboardWidgetCard
            title="Rehearsal Progress"
            icon="trending_up"
            footer={canOpenScheduler ? (
                <Button
                    aria-label="Open Scheduler"
                    onClick={() => openTool('scheduler')}
                    size="sm"
                    variant="ghost"
                >
                    Scheduler
                </Button>
            ) : undefined}
            testId="rehearsal-progress-widget"
        >
            {totalCount === 0 ? (
                <div className="text-center py-6">
                    <Icon name="view_list" size="xl" className="text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No scenes in show structure yet</p>
                </div>
            ) : (
                <div className="space-y-4">
                    <div>
                        <div className="flex items-end justify-between gap-3">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {completedCount}/{totalCount} scenes rehearsed
                            </p>
                            <p className="text-2xl font-bold text-primary">{progress}%</p>
                        </div>
                        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                            <div
                                className="h-full rounded-full bg-primary transition-all duration-500"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    {nextScene ? (
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Next up</p>
                            <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                                Next up: {nextScene.name}
                            </p>
                        </div>
                    ) : (
                        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-3">
                            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                                All active scenes have rehearsal coverage
                            </p>
                        </div>
                    )}
                </div>
            )}
        </DashboardWidgetCard>
    );
};
